import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import makeComponentTrashable from "trashable-react";
import { formatDate, formatAmount } from "Shared/util/formatters";
import { sum } from "Shared/util/calculations";
import { fetchInvoiceGroup } from "Shared/util/web-api";
import Page from "Shared/components/page/page";
import { PdfDownload } from "Shared/components/pdfDownload/pdf-download";
import { useTranslation } from "Shared/util/custom-hooks";
import InvoiceSoldTable from "./details/invoice-sold-table";
import InvoiceMonitoredTable from "./details/invoice-monitored-table";
import ServiceTable from "./details/service-table";
import FinancialTransactionTable from "./details/financial-transaction-table";
import styles from "./group-page.css";
import useCustomerContext from "Contexts/customer-context";

const renderProperty = (label, value) => (
    <div className={styles.property}>
        <span>{label}</span>
        <span>{value}</span>
    </div>
);

const InvoiceGroupPage = ({ registerPromise, match }) => {
    const [summary, setSummary] = useState({});
    const [invoices, setInvoices] = useState([]);
    const [services, setServices] = useState([]);
    const [financialTransactions, setFinancialTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const t = useTranslation((x) => x.transactions);
    const [vat, setVat] = useState();
    const [transactionId, setTransactionId] = useState();
    const { customerData } = useCustomerContext();

    useEffect(() => {
        registerPromise(fetchInvoiceGroup(match.params.id)).then((result) => {
            setSummary(result?.summary || {});
            setInvoices(result?.invoices || []);
            setServices(result?.services || []);
            setFinancialTransactions(result?.financialTransactions || []);
            setVat(result?.summary.taxPercent);
            setTransactionId(result?.summary.transactionId);
            setLoading(false);
        });
    }, [vat]);

    const showBuyingRate = (summary.buyingRate ?? 1) !== 1;

    const invoicesSold = invoices.filter((invoice) => invoice.serviceId !== 2);
    const invoicesMonitored = invoices.filter(
        (invoice) => invoice.serviceId === 2,
    );

    return (
        <Page
            title={t.specificationPayout.replace(
                "${summaryDate}",
                summary.date ? formatDate(summary.date) : "",
            )}
            loading={loading}
        >
            <PdfDownload transactionId={transactionId} />
            <div className={styles.contentContainer}>
                <div className={styles.summarySection}>
                    <div className={styles.paymentMeansContainer}>
                        <div>
                            <span>{t[summary.paymentMeans]}</span>
                            <span>{`${summary.accountNumber}:`}</span>
                        </div>
                        <span>{`${formatAmount(summary.transactionAmount)} ${
                            summary.currencyCode
                        }`}</span>
                    </div>
                    {showBuyingRate && (
                        <div className={styles.buyingRateContainer}>
                            <span>{t.rate}</span>
                            <span>{formatAmount(summary.buyingRate, 4)}</span>
                        </div>
                    )}
                </div>
                <RenderInvoicesSold
                    invoicesSold={invoicesSold}
                    customerData={customerData}
                    t={t}
                    vat={vat}
                />
                {invoicesMonitored.length > 0 && (
                    <div>
                        <h4>{t.monitoredInvoices}</h4>
                        <InvoiceMonitoredTable items={invoicesMonitored} />
                    </div>
                )}
                <div>
                    <h4>{t.otherServices}</h4>
                    <ServiceTable items={services} />
                    {services.length > 0 && (
                        <>
                            <div className={styles.tableSection}>
                                {renderProperty(
                                    t.deductsSumOfOtherServicesExludingVat,
                                    formatAmount(-1 * sum(services, "amount")),
                                )}
                            </div>
                            <div className={styles.tableSection}>
                                {renderProperty(
                                    t.deductsVatOnOtherServices.replace(
                                        "${vat}",
                                        vat,
                                    ),
                                    formatAmount(
                                        -1 * sum(services, "amountVAT"),
                                    ),
                                )}
                            </div>
                        </>
                    )}
                </div>
                <div>
                    <h4>{t.financialTransactions}</h4>
                    <FinancialTransactionTable items={financialTransactions} />
                </div>
                <div className={styles.footerSection}>
                    <div className={styles.paymentMeansContainer}>
                        <div>
                            <span>{t[summary.paymentMeans]}</span>
                            <span>{`${summary.accountNumber}:`}</span>
                        </div>
                        <span>{`${formatAmount(summary.transactionAmount)} ${
                            summary.currencyCode
                        }`}</span>
                    </div>
                </div>
            </div>
        </Page>
    );
};

const RenderInvoicesSold = ({ customerData, invoicesSold, t, vat }) => {
    return (
        <div>
            <h4>{t.invoicesSold}</h4>
            <InvoiceSoldTable items={invoicesSold} />
            {invoicesSold.length > 0 && (
                <>
                    <div className={styles.tableSection}>
                        {renderProperty(
                            t.depositsTotalCommission,
                            formatAmount(
                                -1 * sum(invoicesSold, "commissionAmount"),
                            ),
                        )}
                    </div>
                    <div className={styles.tableSection}>
                        {renderProperty(
                            t.deductsVatOnCommission.replace("${vat}", vat),
                            customerData?.useVat
                                ? formatAmount(
                                      -1 *
                                          (sum(
                                              invoicesSold,
                                              "commissionAmount",
                                          ) *
                                              (vat / 100)),
                                  )
                                : 0,
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

InvoiceGroupPage.propTypes = {
    registerPromise: PropTypes.func.isRequired,
    match: PropTypes.shape({
        params: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
    }).isRequired,
};

export default makeComponentTrashable(InvoiceGroupPage);
